import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  CircleDashed,
  Pause,
  Play,
  Settings2,
  Clock,
  Activity,
  AlertTriangle,
  Database,
  Columns3,
  ArrowRightLeft,
  Zap,
  RotateCcw,
  Eye,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import EditSyncJobDialog from "@/components/syncing/EditSyncJobDialog";
import QueryEditor from "@/components/admin/QueryEditor";

interface PipelineStats {
  SinkedOK?: number;
  RetryTotal?: number;
  SinkedFail?: number;
  DroppedTotal?: number;
  SourcedTotal?: number;
  TransformedOK?: number;
  TransformedFail?: number;
}

interface SyncingJob {
  id: string;
  name: string;
  connection_id: string | null;
  job_name: string | null;
  connection_name: string | null;
  source_table_name: string | null;
  dest_table_name: string | null;
  columns: unknown;
  offset_column: string | null;
  last_sync_start: string | null;
  last_sync_end: string | null;
  last_sync_status: string | null;
  logo: string | null;
  logo_color: string | null;
  no_of_records: number | null;
  paused: boolean | null;
  last_offset: string | null;
  last_stats: PipelineStats | null;
  created_at: string;
  updated_at: string;
}

interface JobHistory {
  id: string;
  source_syncing_job_id: string | null;
  pipeline: string;
  last_run_start_time: string | null;
  last_run_end_time: string | null;
  status: string;
  no_of_records: number | null;
  records_total: number;
  log: string | null;
  last_offset: number;
  last_offset_value: string;
  last_stats: unknown;
  created_at: string;
}

const statusConfig: Record<
  string,
  { icon: typeof CheckCircle2; label: string; color: string; bg: string }
> = {
  completed: { icon: CheckCircle2, label: "SYNCED", color: "text-emerald-400", bg: "bg-emerald-400/10 border-emerald-400/20" },
  success: { icon: CheckCircle2, label: "SYNCED", color: "text-emerald-400", bg: "bg-emerald-400/10 border-emerald-400/20" },
  running: { icon: Loader2, label: "RUNNING", color: "text-blue-400", bg: "bg-blue-400/10 border-blue-400/20" },
  failed: { icon: XCircle, label: "FAILED", color: "text-red-400", bg: "bg-red-400/10 border-red-400/20" },
  error: { icon: AlertTriangle, label: "ERROR", color: "text-red-400", bg: "bg-red-400/10 border-red-400/20" },
  pending: { icon: CircleDashed, label: "PENDING", color: "text-yellow-400", bg: "bg-yellow-400/10 border-yellow-400/20" },
};

const StatusIndicator = ({ status, large }: { status: string; large?: boolean }) => {
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded border font-mono font-semibold tracking-wider",
        config.bg,
        config.color,
        large ? "px-3 py-1 text-xs" : "px-2 py-0.5 text-[11px]",
      )}
    >
      <Icon className={cn(large ? "h-4 w-4" : "h-3 w-3", status === "running" && "animate-spin")} />
      {config.label}
    </span>
  );
};

const PipelineLogo = ({ logo, logoColor }: { logo: string | null; logoColor: string | null }) => {
  const [imgError, setImgError] = useState(false);

  if (logo && !imgError) {
    return (
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg overflow-hidden"
        style={{ backgroundColor: logoColor || "#6366f1" }}
      >
        <img
          src={`https://cdn.simpleicons.org/${logo}/white`}
          alt=""
          style={{ width: 20, height: 20 }}
          className="object-contain"
          onError={() => setImgError(true)}
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
      style={{ backgroundColor: logoColor || "#6366f1" }}
    >
      <Database className="h-5 w-5 text-white" />
    </div>
  );
};

const formatTime = (val: string | null) => {
  if (!val) return "—";
  try {
    return format(new Date(val), "MMM d, yyyy · HH:mm:ss");
  } catch {
    return "—";
  }
};

const computeDuration = (start: string | null, end: string | null) => {
  if (!start || !end) return "—";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const MetricCard = ({ label, value, sub, icon: Icon }: { label: string; value: string | number; sub?: string; icon: typeof Database }) => (
  <div className="rounded-lg border border-border bg-card p-4">
    <div className="flex items-center gap-2 text-muted-foreground mb-2">
      <Icon className="h-3.5 w-3.5" />
      <span className="font-mono text-[10px] uppercase tracking-widest">{label}</span>
    </div>
    <div className="font-mono text-xl font-bold tabular-nums">{typeof value === "number" ? value.toLocaleString() : value}</div>
    {sub && <div className="font-mono text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const PipelineDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [job, setJob] = useState<SyncingJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<JobHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [recordsDefaultQuery, setRecordsDefaultQuery] = useState("");

  const fetchJob = async () => {
    if (!id) return;
    const { data, error } = await supabase
      .from("pipeline_jobs" as any)
      .select("*")
      .eq("id", id)
      .single();
    if (error || !data) {
      toast.error("Pipeline not found");
      navigate("/sync-monitor");
      return;
    }
    setJob(data as unknown as SyncingJob);
    setLoading(false);
  };

  const fetchHistory = async () => {
    if (!id || !job?.name) return;
    setHistoryLoading(true);
    const { data } = await supabase
      .from("pipelinejob_checkpoints" as any)
      .select("*")
      .or(`source_syncing_job_id.eq.${id},pipeline.eq.${job.name}`)
      .order("created_at", { ascending: false })
      .limit(100);
    setHistory((data as unknown as JobHistory[]) || []);
    setHistoryLoading(false);
  };

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchJob(), fetchHistory()]);
    setRefreshing(false);
  };

  useEffect(() => {
    fetchJob();
  }, [id]);

  useEffect(() => {
    if (job) fetchHistory();
  }, [id, job?.name]);

  const togglePause = async () => {
    if (!job) return;
    const newPaused = !job.paused;
    const { error } = await supabase
      .from("pipeline_jobs" as any)
      .update({ paused: newPaused } as any)
      .eq("id", job.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setJob((prev) => prev ? { ...prev, paused: newPaused } : prev);
    toast.success(newPaused ? "Pipeline paused" : "Pipeline resumed");
  };

  const triggerSync = async () => {
    if (!job) return;
    const { error } = await supabase
      .from("pipeline_jobs" as any)
      .update({ last_sync_status: "pending", paused: false } as any)
      .eq("id", job.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setJob((prev) => prev ? { ...prev, last_sync_status: "pending", paused: false } : prev);
    toast.success("Sync triggered");
  };

  const resetOffset = async () => {
    if (!job) return;
    const { error } = await supabase
      .from("pipeline_jobs" as any)
      .update({ last_offset: null } as any)
      .eq("id", job.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    setJob((prev) => prev ? { ...prev, last_offset: null } : prev);
    toast.success("Offset reset — next sync will do a full load");
  };

  const openRecords = () => {
    const table = job?.dest_table_name || job?.source_table_name || "table_name";
    setRecordsDefaultQuery(`SELECT * FROM ${table} ORDER BY id DESC LIMIT 100`);
    setRecordsOpen(true);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center gap-2 text-muted-foreground font-mono text-sm">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading pipeline...
      </div>
    );
  }

  if (!job) return null;

  const colCount = Array.isArray(job.columns) ? job.columns.length : 0;
  const columns = Array.isArray(job.columns) ? (job.columns as any[]) : [];
  const stats = job.last_stats as PipelineStats | null;

  return (
    <div className="flex-1 overflow-auto bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => navigate("/sync-monitor")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <PipelineLogo logo={job.logo} logoColor={job.logo_color} />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-mono text-lg font-bold tracking-tight">{(job.job_name || job.name || "—").split("_").slice(1).join("_") || job.job_name || job.name || "—"}</h1>
                <StatusIndicator status={job.paused ? "pending" : (job.last_sync_status || "pending")} large />
              </div>
              <p className="font-mono text-[11px] text-muted-foreground mt-0.5">
                ID: {job.id} · Connection: {job.connection_name || "—"}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} className="font-mono text-xs gap-1.5">
            <RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
            REFRESH
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="border-b border-border bg-card/30 px-6 py-3">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={togglePause}
            className={cn(
              "font-mono text-xs gap-1.5",
              job.paused
                ? "border-emerald-400/30 text-emerald-400 hover:bg-emerald-400/10 hover:text-emerald-400"
                : "border-yellow-400/30 text-yellow-400 hover:bg-yellow-400/10 hover:text-yellow-400",
            )}
          >
            {job.paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
            {job.paused ? "RESUME" : "PAUSE"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={triggerSync}
            className="font-mono text-xs gap-1.5 border-blue-400/30 text-blue-400 hover:bg-blue-400/10 hover:text-blue-400"
          >
            <Zap className="h-3.5 w-3.5" />
            TRIGGER SYNC
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={resetOffset}
            className="font-mono text-xs gap-1.5 border-orange-400/30 text-orange-400 hover:bg-orange-400/10 hover:text-orange-400"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            RESET OFFSET
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditDialogOpen(true)}
            className="font-mono text-xs gap-1.5"
          >
            <Settings2 className="h-3.5 w-3.5" />
            CONFIGURE
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={openRecords}
            className="font-mono text-xs gap-1.5 border-purple-400/30 text-purple-400 hover:bg-purple-400/10 hover:text-purple-400"
          >
            <Eye className="h-3.5 w-3.5" />
            VIEW RECORDS
          </Button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Metric Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <MetricCard icon={Database} label="Records" value={job.no_of_records || 0} />
          <MetricCard icon={Columns3} label="Columns" value={colCount} />
          <MetricCard icon={Zap} label="Status" value={job.paused ? "Paused" : job.last_sync_status?.toUpperCase() || "—"} />
          {stats && (
            <>
              <MetricCard icon={ArrowRightLeft} label="Sourced" value={stats.SourcedTotal ?? 0} />
              <MetricCard icon={CheckCircle2} label="Sinked OK" value={stats.SinkedOK ?? 0} />
              <MetricCard icon={XCircle} label="Sinked Fail" value={stats.SinkedFail ?? 0} />
            </>
          )}
        </div>

        {/* Pipeline Stats */}
        {stats && (
          <div className="rounded-lg border border-border bg-card">
            <div className="border-b border-border px-4 py-2.5">
              <h2 className="font-mono text-xs font-semibold tracking-wider text-muted-foreground uppercase flex items-center gap-2">
                <Activity className="h-3.5 w-3.5" />
                Pipeline Stats
              </h2>
            </div>
            <div className="divide-y divide-border">
              <InfoRow label="Sourced Total" value={String(stats.SourcedTotal ?? 0)} mono />
              <InfoRow label="Transformed OK" value={String(stats.TransformedOK ?? 0)} mono />
              <InfoRow label="Transformed Fail" value={String(stats.TransformedFail ?? 0)} mono />
              <InfoRow label="Sinked OK" value={String(stats.SinkedOK ?? 0)} mono />
              <InfoRow label="Sinked Fail" value={String(stats.SinkedFail ?? 0)} mono />
              <InfoRow label="Dropped Total" value={String(stats.DroppedTotal ?? 0)} mono />
              <InfoRow label="Retry Total" value={String(stats.RetryTotal ?? 0)} mono />
            </div>
          </div>
        )}

        {/* Pipeline Info */}
        <div className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-2.5">
            <h2 className="font-mono text-xs font-semibold tracking-wider text-muted-foreground uppercase">Pipeline Configuration</h2>
          </div>
          <div className="divide-y divide-border">
            <InfoRow label="Source Table" value={job.source_table_name || "—"} mono />
            <InfoRow label="Destination Table" value={job.dest_table_name || "auto-generated"} mono />
            <InfoRow label="Offset Column" value={job.offset_column || "none"} mono />
            <InfoRow label="Last Offset" value={job.last_offset || "none"} mono />
            <InfoRow label="Last Sync Start" value={formatTime(job.last_sync_start)} />
            <InfoRow label="Last Sync End" value={formatTime(job.last_sync_end)} />
            <InfoRow
              label="Duration"
              value={computeDuration(job.last_sync_start, job.last_sync_end)}
            />
            <InfoRow label="Created" value={formatTime(job.created_at)} />
          </div>
        </div>

        {/* Columns */}
        {columns.length > 0 && (
          <div className="rounded-lg border border-border bg-card">
            <div className="border-b border-border px-4 py-2.5">
              <h2 className="font-mono text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                Synced Columns ({columns.length})
              </h2>
            </div>
            <div className="p-4">
              <div className="flex flex-wrap gap-1.5">
                {columns.map((col: any, i: number) => {
                  const name = col.column_name || col;
                  const type = col.data_type || "";
                  const isOffset = name === job.offset_column;
                  return (
                    <span
                      key={i}
                      className={cn(
                        "inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[11px]",
                        isOffset
                          ? "border-primary/40 bg-primary/10 text-primary"
                          : "border-border bg-muted/30 text-foreground",
                      )}
                    >
                      {name}
                      {type && <span className="text-muted-foreground text-[9px]">{type}</span>}
                      {isOffset && <span className="text-[9px] font-bold">OFFSET</span>}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* History */}
        <div className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-2.5 flex items-center justify-between">
            <h2 className="font-mono text-xs font-semibold tracking-wider text-muted-foreground uppercase flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" />
              Execution History
            </h2>
            <span className="font-mono text-[10px] text-muted-foreground">
              {history.length} run{history.length !== 1 ? "s" : ""}
            </span>
          </div>

          {historyLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground gap-2 font-mono text-sm">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading history...
            </div>
          ) : history.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground font-mono text-sm">No execution history yet</p>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-mono text-[10px] uppercase tracking-widest">Status</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-widest">Start</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-widest">End</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-widest">Duration</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-widest text-right">Records</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-widest">Last Offset</TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-widest">Log</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id} className="font-mono text-xs">
                      <TableCell>
                        <StatusIndicator status={h.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-[11px]">{formatTime(h.last_run_start_time)}</TableCell>
                      <TableCell className="text-muted-foreground text-[11px]">{formatTime(h.last_run_end_time)}</TableCell>
                      <TableCell className="tabular-nums">{computeDuration(h.last_run_start_time, h.last_run_end_time)}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{h.no_of_records?.toLocaleString() || "0"}</TableCell>
                      <TableCell className="text-muted-foreground text-[11px] max-w-[120px] truncate" title={h.last_offset_value || String(h.last_offset) || ""}>
                        {h.last_offset_value || h.last_offset || "—"}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        {h.log ? (
                          <code className="block truncate text-[10px] text-muted-foreground bg-muted/30 rounded px-1.5 py-0.5" title={h.log}>
                            {h.log}
                          </code>
                        ) : (
                          <span className="text-muted-foreground/50">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <EditSyncJobDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        job={job}
        onSaved={() => {
          fetchJob();
          setEditDialogOpen(false);
        }}
      />

      {/* View Records Dialog */}
      <Dialog open={recordsOpen} onOpenChange={setRecordsOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 overflow-hidden flex flex-col">
          {recordsOpen && (
            <QueryEditor
              editingQuery={{ sql_query: recordsDefaultQuery, name: `Records: ${job.dest_table_name || job.source_table_name}`, generation_mode: "manual" }}
              onClose={() => setRecordsOpen(false)}
              onSaved={() => setRecordsOpen(false)}
              saveToDb={false}
              autoRun
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InfoRow = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-center justify-between px-4 py-2.5 text-sm">
    <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider">{label}</span>
    <span className={cn("text-foreground", mono && "font-mono text-xs")}>{value}</span>
  </div>
);

export default PipelineDetail;
